/**
 * Shiny Leaf — ADA / WCAG 2.1 AA accessibility enhancements
 * Fixes applied at DOM level for issues found in the site-wide audit:
 *  1. Icon-only links (search, cart, account) with no accessible name
 *  2. Image-only links with no accessible name (e.g. mega-menu image links)
 *  3. Generic "Add to cart" / "Choose options" buttons — adds product context
 *  4. <img> elements missing an alt attribute entirely (marked decorative)
 *  5. Links whose visible text is a raw URL (e.g. "Powered by Shopify")
 *  6. Pages with no <h1> — promotes the first main heading to level 1
 *  7. Re-runs on dynamic content (cart drawer, quick view) via MutationObserver
 */
(function () {
  'use strict';

  function hasAccessibleName(el) {
    if (el.getAttribute('aria-label') || el.getAttribute('aria-labelledby') || el.getAttribute('title')) return true;
    if (el.querySelector('img[alt]:not([alt=""])')) return true;
    if (el.querySelector('svg title, svg [aria-label]')) return true;
    var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
    return text.length > 0;
  }

  function humanize(str) {
    return str
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function labelIconLinks(root) {
    var known = [
      ['a[href$="/search"], a[href*="/search?"]', 'Search'],
      ['a[href$="/cart"]', 'Cart'],
      ['a[href$="/account"], a[href*="/account/login"]', 'Account']
    ];
    known.forEach(function (pair) {
      root.querySelectorAll(pair[0]).forEach(function (a) {
        if (!hasAccessibleName(a)) a.setAttribute('aria-label', pair[1]);
      });
    });

    root.querySelectorAll('a[href]').forEach(function (a) {
      if (hasAccessibleName(a)) return;
      var href = a.getAttribute('href');
      if (!href || href === '#' || href.indexOf('javascript:') === 0) return;
      var path;
      try { path = new URL(a.href, window.location.origin).pathname; } catch (e) { return; }
      var seg = path.split('/').filter(Boolean).pop();
      if (seg) a.setAttribute('aria-label', humanize(decodeURIComponent(seg)));
    });
  }

  function labelCardButtons(root) {
    var actions = ['add to cart', 'choose options', 'quick view', 'shop', 'shop now'];
    root.querySelectorAll('button, a.btn, a[role="button"]').forEach(function (btn) {
      if (btn.getAttribute('data-a11y-labeled')) return;
      var text = (btn.textContent || '').replace(/\s+/g, ' ').trim();
      if (!text || actions.indexOf(text.toLowerCase()) === -1) return;
      var card = btn.closest('.card, .card-wrapper, .product-card, .card-product, li, .grid__item, [class*="card"]');
      if (!card) return;
      var titleLink = card.querySelector('a[href*="/products/"], .card__heading a, h3 a');
      if (!titleLink) return;
      var title = (titleLink.textContent || '').replace(/\s+/g, ' ').trim();
      if (!title || title === text) return;
      btn.setAttribute('aria-label', text + ' — ' + title);
      btn.setAttribute('data-a11y-labeled', 'true');
    });
  }

  function fixMissingAlt(root) {
    root.querySelectorAll('img:not([alt])').forEach(function (img) {
      img.setAttribute('alt', '');
      img.setAttribute('role', 'presentation');
    });
  }

  function fixRawUrlLinks(root) {
    root.querySelectorAll('a[href]').forEach(function (a) {
      var text = (a.textContent || '').trim();
      if (!/^https?:\/\//.test(text)) return;
      if (a.getAttribute('aria-label')) return;
      try {
        var u = new URL(a.href);
        var host = u.hostname.replace(/^www\./, '');
        if (host.indexOf('shopify.com') !== -1) a.setAttribute('aria-label', 'Powered by Shopify');
        else a.setAttribute('aria-label', host);
      } catch (e) { /* noop */ }
    });
  }

  function ensureH1() {
    if (document.querySelector('h1')) return;
    var main = document.getElementById('MainContent') || document.querySelector('main');
    if (!main) return;
    var h = main.querySelector('h2, .h1, .h2, [class*="title"]');
    if (h) {
      h.setAttribute('role', 'heading');
      h.setAttribute('aria-level', '1');
    }
  }

  var scheduled = false;
  function runAll() {
    var root = document;
    labelIconLinks(root);
    labelCardButtons(root);
    fixMissingAlt(root);
    fixRawUrlLinks(root);
    ensureH1();
  }

  function scheduleRun() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      runAll();
    });
  }

  function init() {
    runAll();
    var observer = new MutationObserver(scheduleRun);
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
